import { useCallback, useEffect, useRef, useState, UIEvent } from 'react';

export type ScrollDirection = 'up' | 'down';

type Options = {
  threshold?: number;
  settleMs?: number;
  debounceMs?: number;
  initialDirection?: ScrollDirection;
  onChange?: (direction: ScrollDirection) => void;
};

const DEFAULT_THRESHOLD = 24;
const DEFAULT_SETTLE_MS = 250;
const DEFAULT_DEBOUNCE_MS = 0;

export function useScrollDirection(options: Options = {}) {
  const {
    threshold = DEFAULT_THRESHOLD,
    settleMs = DEFAULT_SETTLE_MS,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    initialDirection = 'up',
    onChange,
  } = options;

  const [direction, setDirection] = useState<ScrollDirection>(initialDirection);
  const directionRef = useRef<ScrollDirection>(initialDirection);
  const pivotScrollTop = useRef(0);
  const lastChangeAt = useRef(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();

  const updateDirection = useCallback(
    (next: ScrollDirection) => {
      if (directionRef.current === next) {
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
          debounceTimer.current = undefined;
        }
        return;
      }

      const apply = () => {
        directionRef.current = next;
        lastChangeAt.current = Date.now();
        if (onChange) {
          onChange(next);
        }
        setDirection(next);
      };

      if (debounceMs <= 0) {
        apply();
        return;
      }

      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(apply, debounceMs);
    },
    [debounceMs, onChange],
  );

  useEffect(
    () => () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    },
    [],
  );

  const handleScroll = useCallback(
    (scrollTop: number) => {
      const position = Math.max(scrollTop, 0);

      if (position <= 0) {
        pivotScrollTop.current = 0;
        updateDirection('up');
        return;
      }

      const nextDirection: ScrollDirection =
        position > pivotScrollTop.current ? 'down' : 'up';

      if (nextDirection === directionRef.current) {
        pivotScrollTop.current = position;
        return;
      }

      if (Date.now() - lastChangeAt.current < settleMs) {
        pivotScrollTop.current = position;
        return;
      }

      if (Math.abs(position - pivotScrollTop.current) < threshold) return;

      pivotScrollTop.current = position;
      updateDirection(nextDirection);
    },
    [threshold, settleMs, updateDirection],
  );

  const onScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      handleScroll(event.currentTarget.scrollTop);
    },
    [handleScroll],
  );

  return { direction, onScroll, handleScroll };
}

export default useScrollDirection;
